const steps = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    title: 'Risk Assessment & Audit',
    description: 'We map your entire infrastructure, identify vulnerabilities, and expose gaps in your current backup and recovery setup — including silent failures most businesses never know exist.',
    highlight: 'Free with no commitment',
  },
  {
    number: '02',
    icon: 'ri-drafting-compass-line',
    title: 'Infrastructure Design',
    description: 'We build a custom protection plan: backup architecture, DR strategy, monitoring coverage, and response protocols — all engineered around your specific business requirements.',
    highlight: 'Tailored to your risk profile',
  },
  {
    number: '03',
    icon: 'ri-radar-line',
    title: 'Deployment & 24/7 Monitoring',
    description: 'Systems go live. Backups run and are verified. Monitoring tools activate. Our local team watches your infrastructure around the clock — proactively resolving issues before they become outages.',
    highlight: 'Always-on protection',
  },
  {
    number: '04',
    icon: 'ri-timer-flash-line',
    title: 'Rapid Response & Recovery',
    description: 'When something goes wrong — hardware failure, ransomware, data corruption — we respond immediately. Average recovery time under 15 minutes. Your business stays operational.',
    highlight: 'Avg. recovery &lt; 15 min',
  },
];

const ProcessFlow = () => {
  return (
    <section className="py-24 lg:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-teal-500/5 blur-3xl rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="max-w-2xl mb-20">
          <span className="text-teal-400 font-bold text-xs uppercase tracking-widest block mb-4">
            How It Works
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-[1.05] tracking-tight mb-5">
            From Exposed to<br />
            <span className="text-teal-400">Fully Protected</span>
          </h2>
          <p className="text-slate-500 text-base leading-relaxed">
            A structured, repeatable system that eliminates guesswork and builds real resilience into your business operations.
          </p>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-4 gap-0 relative">

          {/* Connector line — desktop only */}
          <div className="absolute top-[52px] left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-px bg-gradient-to-r from-teal-500/30 via-teal-500/60 to-teal-500/30 hidden lg:block pointer-events-none" />

          {steps.map((step, i) => (
            <div key={i} className="relative flex flex-col items-start lg:items-center lg:text-center px-0 lg:px-6 xl:px-8 pb-10 lg:pb-0">

              {/* Step number + icon circle */}
              <div className="relative mb-6 flex-shrink-0">
                {/* Outer ring */}
                <div className="w-[52px] h-[52px] rounded-full border border-teal-500/40 bg-slate-950 flex items-center justify-center relative z-10">
                  <div className="w-[38px] h-[38px] rounded-full bg-teal-500/12 border border-teal-500/30 flex items-center justify-center">
                    <i className={`${step.icon} text-teal-400 text-base`} />
                  </div>
                </div>
                {/* Step number badge */}
                <span className="absolute -top-2 -right-2 bg-slate-950 border border-slate-700 text-teal-400 text-[10px] font-extrabold rounded-full w-5 h-5 flex items-center justify-center tracking-tight">
                  {i + 1}
                </span>
              </div>

              {/* Content */}
              <div>
                <h3 className="text-white font-bold text-base mb-3 leading-tight">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">{step.description}</p>
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold text-teal-400 bg-teal-500/8 border border-teal-500/20 rounded-full px-3 py-1.5"
                  dangerouslySetInnerHTML={{ __html: step.highlight }}
                />
              </div>

              {/* Mobile connector */}
              {i < steps.length - 1 && (
                <div className="lg:hidden w-px h-8 bg-teal-500/30 mt-6 self-center" />
              )}
            </div>
          ))}
        </div>

        {/* Bottom stats bar */}
        <div className="mt-20 pt-10 border-t border-slate-800/60">
          <div className="grid sm:grid-cols-3 gap-8 lg:gap-0 lg:divide-x divide-slate-800">
            {[
              { value: '< 4 min', label: 'Average Response Time', sub: 'From alert to action' },
              { value: '< 15 min', label: 'Average Recovery Time', sub: 'Systems back online fast' },
              { value: '99.9%', label: 'Uptime SLA', sub: 'Engineered reliability' },
            ].map((stat) => (
              <div key={stat.value} className="lg:px-12 first:pl-0 last:pr-0 text-center sm:text-left lg:text-center">
                <p className="text-4xl font-extrabold text-teal-400 tracking-tight mb-1">{stat.value}</p>
                <p className="text-white font-bold text-sm mb-0.5">{stat.label}</p>
                <p className="text-slate-600 text-xs">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessFlow;
